import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAssets, useCountries, useIndustries, usePartners } from '@/api/queries';
import { useSession } from '@/session/SessionContext';
import { useDebouncedSearch } from '@/ui/useDebouncedSearch';
import { useStoredChoice } from '@/ui/useStoredChoice';

import { applyPatch, catalogSearch, isFiltered, parseCatalogView, sortPatch, toListParams, type CatalogView } from './catalogParams';

export type CatalogLayout = 'list' | 'tiles';
const LAYOUTS: readonly CatalogLayout[] = ['list', 'tiles'];

/** The catalog list's URL-backed view, its reads and the tag lists its filters offer. */
export function useCatalogPage() {
  const { can, user } = useSession();
  const [search, setSearch] = useSearchParams();
  const view = parseCatalogView(search);
  const update = (patch: Partial<CatalogView>) => setSearch(catalogSearch(applyPatch(view, patch)), { replace: true });
  const [query, setQuery] = useDebouncedSearch(view.q, update);
  const [layout, setLayout] = useStoredChoice<CatalogLayout>(`wynxx.assets.view.${user?.id ?? 'anon'}`, LAYOUTS, 'list');
  const [moreOpen, setMoreOpen] = useState(false);

  const list = useAssets(toListParams(view));
  const industries = useIndustries({ enabled: can('industry.view') });
  const partners = usePartners();
  const countries = useCountries();

  return {
    view,
    update,
    onSort: (key: string) => update(sortPatch(view, key)),
    query,
    setQuery,
    layout,
    setLayout,
    moreOpen,
    toggleMore: () => setMoreOpen((open) => !open),
    list,
    filtered: isFiltered(view),
    clearFilters: () => {
      setQuery('');
      setSearch(new URLSearchParams(), { replace: true });
    },
    canDrafts: can('asset.view.draft'),
    canCreate: can('asset.manage'),
    industries: (industries.data?.industries ?? []).filter((i) => i.status === 'approved' && i.is_active),
    partners: partners.data?.partners ?? [],
    countries: countries.data ?? [],
  };
}

export type CatalogPageState = ReturnType<typeof useCatalogPage>;
